'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { requestPasswordReset } from '@/lib/auth/forgot-password';
import { forgotPasswordSchema } from '@/lib/validation/auth';

type FormValues = { email: string };

export default function ForgotForm() {
  const [done, setDone] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  async function onSubmit(values: FormValues) {
    // Goes through our server-side /auth/confirm flow rather than
    // Supabase's built-in PKCE callback — see lib/auth/forgot-password.
    // The response is intentionally enumeration-safe (always succeeds
    // from the caller's POV), so we just flip to the confirmation UI.
    await requestPasswordReset({ email: values.email });
    setDone(true);
  }

  if (done) {
    return <p className="text-sm">If that email exists, a reset link is on its way.</p>;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <div>
        <Label htmlFor="email">Email</Label>
        <Input id="email" type="email" {...register('email')} />
        {errors.email && <p className="text-xs text-red-600">{errors.email.message}</p>}
      </div>
      <Button type="submit" className="w-full" disabled={isSubmitting}>
        Send reset link
      </Button>
    </form>
  );
}
